import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasStoredSession } from "@/lib/authStorage";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowRight, UtensilsCrossed, Dumbbell, TrendingUp, Camera, CalendarDays, CreditCard,
  MessageSquare, Users, Check, Zap, Sparkles, Loader2, Calculator, Star, ChevronDown,
  UserPlus, Settings, Rocket, HelpCircle, ShoppingCart, Pill, ClipboardList, Flame,
  BookOpen, ListChecks, Brain, Download, Bell, FileStack, Footprints, Layers, Bike,
  Waves, Activity, Gauge, Droplets, Link2, RefreshCw, Target,
} from "lucide-react";
import axios from "axios";

const disciplinePills = [
  { icon: Dumbbell, label: "Force", color: "bg-slate-900 text-white" },
  { icon: Footprints, label: "Course", color: "bg-emerald-600 text-white" },
  { icon: Bike, label: "Vélo", color: "bg-sky-600 text-white" },
  { icon: Waves, label: "Natation", color: "bg-cyan-600 text-white" },
  { icon: Layers, label: "Hybride", color: "bg-violet-600 text-white" },
  { icon: Target, label: "Général", color: "bg-amber-600 text-white" },
];

const newFeatures = [
  {
    icon: Layers,
    title: "Programmes multi-sports",
    desc: "Force, course, vélo, natation, hybride ou général — la discipline est sur le plan, pas sur l'élève. Prescriptions en kg, % TM, allure, watts ou CSS.",
    tag: "Training",
    color: "emerald",
  },
  {
    icon: Gauge,
    title: "Références endurance & force",
    desc: "FTP, allure seuil, CSS, VMA côté endurance ; squat / bench / deadlift TM côté force. Les cibles se calculent automatiquement dans le plan et le journal.",
    tag: "Training",
    color: "sky",
  },
  {
    icon: Activity,
    title: "Journal de séances",
    desc: "L'élève logue séries, charges, distance, allure ou puissance. Volume, km et durée agrégés — visible par le coach dans la fiche élève.",
    tag: "Training",
    color: "orange",
  },
  {
    icon: Target,
    title: "Adhérence entraînement",
    desc: "Vue coach : % de séances loguées vs prévu sur 30 jours, par discipline. Identifiez qui décroche avant qu'il ne disparaisse.",
    tag: "Coach",
    color: "violet",
  },
  {
    icon: Bell,
    title: "Charges de demain",
    desc: "Notification push et carte dashboard : rappel des cibles du lendemain (kg, allure, watts) selon le programme actif.",
    tag: "Élève",
    color: "blue",
  },
  {
    icon: RefreshCw,
    title: "Substitution IA (blessure)",
    desc: "Adaptez un programme existant par IA en cas de blessure ou contrainte — la substitution respecte la discipline du plan.",
    tag: "IA",
    color: "purple",
  },
  {
    icon: ShoppingCart,
    title: "Liste de courses unifiée",
    desc: "Aliments du plan diet + compléments prescrits, regroupés en une liste exportable vers Notes ou WhatsApp.",
    tag: "Nutrition",
    color: "emerald",
  },
  {
    icon: Brain,
    title: "Compléments × repas (IA)",
    desc: "L'IA lit le journal repas réel et propose quand prendre chaque complément — ex. oméga-3 au dîner.",
    tag: "IA",
    color: "purple",
  },
  {
    icon: ListChecks,
    title: "Adhérence compléments",
    desc: "Checklist quotidienne côté élève et vue coach avec le % de jours où toute la routine est validée.",
    tag: "Nutrition",
    color: "indigo",
  },
  {
    icon: ClipboardList,
    title: "Journal repas",
    desc: "L'élève consigne ses repas ; le coach consulte la conformité et alimente les analyses IA.",
    tag: "Nutrition",
    color: "orange",
  },
  {
    icon: Droplets,
    title: "Bilan sanguin",
    desc: "Import et suivi des analyses sanguines par élève — historique, tendances et dossier médical intégré.",
    tag: "Medical",
    color: "cyan",
  },
  {
    icon: Link2,
    title: "Multi-comptes sécurisés",
    desc: "Coach ou élève avec plusieurs profils ? Basculez entre comptes sans vous reconnecter — jetons chiffrés localement.",
    tag: "Compte",
    color: "slate",
  },
  {
    icon: Download,
    title: "PDF brandés",
    desc: "Plans diet, programmes multi-sports, dossiers élève et rapports mensuels (km, volume, disciplines) au design COACH.",
    tag: "Export",
    color: "slate",
  },
];

const coachHighlights = [
  { icon: Users, title: "Gestion élèves", desc: "Dossiers complets, code coach, objectifs, progression et rapports mensuels exportables." },
  { icon: Flame, title: "Check-ins quotidiens", desc: "Humeur, énergie, sommeil et adhérence — vue agrégée de toute la cohorte." },
  { icon: Activity, title: "Adhérence entraînement", desc: "Séances loguées vs prévu, km, volume force, dernière séance — par discipline." },
  { icon: Layers, title: "Programmes multi-sports", desc: "6 disciplines, refs FTP/allure/CSS, éditeur hybride, IA et substitution blessure." },
  { icon: UtensilsCrossed, title: "Plans alimentaires", desc: "Macros, repas détaillés, modèles réutilisables et génération IA." },
  { icon: Pill, title: "Compléments & adhérence", desc: "Catalogue, horaires, corrélation repas IA et % de jours validés." },
  { icon: Droplets, title: "Bilan sanguin", desc: "Suivi des analyses par élève, intégré au dossier médical." },
  { icon: Sparkles, title: "Assistant IA", desc: "Diet, training multi-sports, compléments, macros, bilans et chat élève." },
  { icon: FileStack, title: "Modèles & bibliothèque", desc: "Modèles de plans, exercices, vidéos — dupliquez en un clic." },
  { icon: CreditCard, title: "Paiements Stripe", desc: "Forfaits, séances et abonnements encaissés en ligne." },
];

const studentHighlights = [
  { icon: UtensilsCrossed, title: "Plan alimentaire", desc: "Repas, macros et export PDF brandé." },
  { icon: ShoppingCart, title: "Liste de courses", desc: "Diet + compléments, prête pour Notes ou WhatsApp." },
  { icon: Pill, title: "Mes compléments", desc: "Horaires, checklist du jour et conseils IA selon vos repas." },
  { icon: ClipboardList, title: "Journal repas", desc: "Consignez vos repas pour un suivi précis avec votre coach." },
  { icon: Layers, title: "Programme multi-sports", desc: "Muscu, course, vélo, nage ou hybride — cibles perso et vidéos exercices." },
  { icon: Activity, title: "Suivi des séances", desc: "Loguez force ou endurance ; consultez l'historique km, watts et volume." },
  { icon: Bell, title: "Charges de demain", desc: "Rappel des cibles du lendemain sur le dashboard et en notification push." },
  { icon: Flame, title: "Check-ins", desc: "Quotidiens et hebdomadaires — partagez votre ressenti en 30 secondes." },
  { icon: TrendingUp, title: "Progression", desc: "Poids, mensurations, photos avant/après et objectif cible." },
  { icon: Sparkles, title: "Chat IA", desc: "Questions nutrition, training et compléments à l'assistant intégré." },
];

const features = [
  { icon: Layers, title: "Training multi-sports", desc: "6 disciplines, prescriptions adaptées, éditeur hybride, refs endurance/force et génération IA par sport.", span: "md:col-span-2", highlight: true, badge: "NOUVEAU" },
  { icon: Activity, title: "Journal & adhérence", desc: "Séances loguées, % d'adhérence coach, charges demain et rapports mensuels km/volume.", span: "md:col-span-1", highlight: true, badge: "NOUVEAU" },
  { icon: UtensilsCrossed, title: "Plans Diététiques", desc: "Macros, repas structurés, modèles réutilisables et export PDF.", span: "md:col-span-1" },
  { icon: Pill, title: "Compléments", desc: "Catalogue, prescriptions, horaires IA et adhérence quotidienne.", span: "md:col-span-1", highlight: true, badge: "NOUVEAU" },
  { icon: ShoppingCart, title: "Liste de courses", desc: "Diet + compléments fusionnés — Notes, WhatsApp ou fichier.", span: "md:col-span-1", highlight: true, badge: "NOUVEAU" },
  { icon: Sparkles, title: "Assistant IA", desc: "Plans diet & training, substitution blessure, compléments × repas, macros et chat.", span: "md:col-span-1", highlight: true, badge: "IA" },
  { icon: ClipboardList, title: "Journal repas", desc: "Conformité mesurée par le coach, analyses IA personnalisées.", span: "md:col-span-2" },
  { icon: Droplets, title: "Bilan sanguin", desc: "Import et suivi des analyses, intégré au dossier élève.", span: "md:col-span-1" },
  { icon: TrendingUp, title: "Progression", desc: "Poids, mensurations, objectifs, check-ins et rapports mensuels.", span: "md:col-span-1" },
  { icon: Camera, title: "Photos de suivi", desc: "Évolution visuelle partagée, intégrée au dossier.", span: "md:col-span-1" },
  { icon: BookOpen, title: "Bibliothèque exercices", desc: "Catalogue perso, vidéos liées automatiquement aux exercices du plan.", span: "md:col-span-1" },
  { icon: CalendarDays, title: "Calendrier & visio", desc: "Séances de coaching planifiées avec lien visio intégré.", span: "md:col-span-1" },
  { icon: MessageSquare, title: "Messagerie", desc: "Chat temps réel, pièces jointes et résumés IA de conversation.", span: "md:col-span-1" },
  { icon: Bell, title: "Notifications push", desc: "Rappels check-in, charges demain, messages — PWA installable.", span: "md:col-span-1" },
  { icon: Link2, title: "Multi-comptes", desc: "Bascule entre profils coach ou élève sans reconnexion.", span: "md:col-span-1" },
  { icon: CreditCard, title: "Paiements Stripe", desc: "Forfaits, séances, abonnements et profil public coach.", span: "md:col-span-1" },
];

const newFeatureColors = {
  emerald: { card: "from-emerald-50 to-teal-50 border-emerald-200", icon: "bg-emerald-100 text-emerald-600", tag: "bg-emerald-100 text-emerald-700" },
  purple: { card: "from-purple-50 to-violet-50 border-purple-200", icon: "bg-purple-100 text-purple-600", tag: "bg-purple-100 text-purple-700" },
  blue: { card: "from-blue-50 to-indigo-50 border-blue-200", icon: "bg-blue-100 text-blue-600", tag: "bg-blue-100 text-blue-700" },
  indigo: { card: "from-indigo-50 to-blue-50 border-indigo-200", icon: "bg-indigo-100 text-indigo-600", tag: "bg-indigo-100 text-indigo-700" },
  orange: { card: "from-orange-50 to-amber-50 border-orange-200", icon: "bg-orange-100 text-orange-600", tag: "bg-orange-100 text-orange-700" },
  slate: { card: "from-slate-50 to-gray-50 border-slate-200", icon: "bg-slate-100 text-slate-600", tag: "bg-slate-100 text-slate-700" },
  sky: { card: "from-sky-50 to-blue-50 border-sky-200", icon: "bg-sky-100 text-sky-600", tag: "bg-sky-100 text-sky-700" },
  violet: { card: "from-violet-50 to-purple-50 border-violet-200", icon: "bg-violet-100 text-violet-600", tag: "bg-violet-100 text-violet-700" },
  cyan: { card: "from-cyan-50 to-teal-50 border-cyan-200", icon: "bg-cyan-100 text-cyan-600", tag: "bg-cyan-100 text-cyan-700" },
};

const coachPlans = [
  {
    id: "basic",
    name: "Basic",
    monthlyPrice: "29.99",
    annualPrice: "299.90",
    annualMonthly: "24.99",
    description: "Idéal pour démarrer",
    maxStudents: "5 élèves",
    features: [
      "Jusqu'à 5 élèves",
      "Jusqu'à 5 forfaits personnalisés",
      "Plans alimentaires, compléments & training multi-sports",
      "Journal repas, séances & check-ins",
      "Messagerie intégrée",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    monthlyPrice: "39.99",
    annualPrice: "399.90",
    annualMonthly: "33.33",
    description: "Pour les coachs ambitieux",
    popular: true,
    maxStudents: "Illimité",
    features: [
      "Élèves illimités",
      "Forfaits personnalisés illimités",
      "Training multi-sports (6 disciplines) + adhérence séances",
      "Compléments, liste de courses & corrélation repas IA",
      "Journal repas, check-ins & rapports mensuels (km, volume)",
      "Bibliothèque exercices, vidéos, modèles & bilan sanguin",
      "Charges demain, substitution IA blessure & PDF brandés",
      "Paiements Stripe & ✨ IA complète (diet, training, chat)",
    ],
  },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function getDashboardPath(role) {
  if (role === "admin") return "/admin/dashboard";
  if (role === "coach" || role === "student") return `/${role}/dashboard`;
  return "/dashboard";
}

function MacroCalculator() {
  const [form, setForm] = useState({ gender: 'male', age: '', weight: '', height: '', activity: 'moderate', goal: 'maintain' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.age || !form.weight || !form.height) { setError('Veuillez remplir tous les champs.'); return; }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/calculate-macros`, {
        gender: form.gender,
        age: parseInt(form.age),
        weight: parseFloat(form.weight),
        height: parseFloat(form.height),
        activity_level: form.activity,
        goal: form.goal
      });
      setResult(res.data);
    } catch {
      setError('Erreur lors du calcul. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const activityOptions = [
    { value: 'sedentary', label: 'Sédentaire' },
    { value: 'light', label: 'Légèrement actif' },
    { value: 'moderate', label: 'Modérément actif' },
    { value: 'active', label: 'Actif' },
    { value: 'very_active', label: 'Très actif' },
  ];

  const goalOptions = [
    { value: 'lose', label: 'Perdre du poids' },
    { value: 'maintain', label: 'Maintenir' },
    { value: 'gain', label: 'Prendre du muscle' },
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" /> Propulsé par l'IA
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Calculateur de Macros</h2>
          <p className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto">Entrez votre profil et votre objectif — notre IA calcule vos macros idéales en quelques secondes.</p>
        </div>

        <Card className="p-6 md:p-8 border-purple-200 shadow-lg shadow-purple-500/5">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Sexe</label>
                <select value={form.gender} onChange={e => handleChange('gender', e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none">
                  <option value="male">Homme</option>
                  <option value="female">Femme</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Âge</label>
                <input type="number" placeholder="25" value={form.age} onChange={e => handleChange('age', e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Poids (kg)</label>
                <input type="number" step="0.1" placeholder="75" value={form.weight} onChange={e => handleChange('weight', e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Taille (cm)</label>
                <input type="number" placeholder="175" value={form.height} onChange={e => handleChange('height', e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Niveau d'activité</label>
                <select value={form.activity} onChange={e => handleChange('activity', e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none">
                  {activityOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Objectif</label>
                <select value={form.goal} onChange={e => handleChange('goal', e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none">
                  {goalOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <Button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 text-base shadow-lg shadow-purple-500/20">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calcul en cours...</> : <><Calculator className="w-4 h-4 mr-2" /> Calculer mes macros</>}
            </Button>
          </form>

          {result && (
            <div className="mt-8 pt-6 border-t border-purple-100">
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" /> Vos macros recommandées
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{result.calories}</p>
                  <p className="text-xs text-orange-600/70 font-medium mt-1">kcal / jour</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{result.protein}g</p>
                  <p className="text-xs text-blue-600/70 font-medium mt-1">Protéines</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{result.carbs}g</p>
                  <p className="text-xs text-green-600/70 font-medium mt-1">Glucides</p>
                </div>
                <div className="bg-pink-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-pink-600">{result.fat}g</p>
                  <p className="text-xs text-pink-600/70 font-medium mt-1">Lipides</p>
                </div>
              </div>
              {result.explanation && (
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-xl p-4">{result.explanation}</p>
              )}
              <p className="text-xs text-slate-400 mt-3 text-center">Ces valeurs sont des estimations. Pour un suivi personnalisé, inscrivez-vous et travaillez avec un coach.</p>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

const staticCoachTestimonials = [
  { id: "static-coach-1", initials: "ML", name: "Marc L.", role: "Coach fitness & course", quote: "Je coach de la force et de la course sur la même plateforme. L'adhérence séances et les refs allure/FTP me font gagner un temps fou en suivi.", color: "bg-blue-600", rating: 5 },
  { id: "static-coach-2", initials: "SC", name: "Sophie C.", role: "Coach nutrition", quote: "Compléments, journal repas et adhérence au même endroit — je vois qui applique vraiment. Les rapports PDF avec km et volume font très pro.", color: "bg-purple-600", rating: 5 },
  { id: "static-coach-3", initials: "JR", name: "Julien R.", role: "Coach triathlon", quote: "Plans hybrides muscu + vélo + course, charges de demain pour mes élèves, substitution IA quand quelqu'un se blesse. C'est devenu mon outil principal.", color: "bg-violet-600", rating: 5 },
];

const staticStudentTestimonials = [
  { id: "static-student-1", initials: "TD", name: "Thomas D.", role: "Élève", quote: "La liste de courses avec mes compléments, c'est un game changer. Et le rappel des charges de demain — je sais exactement quoi faire avant d'aller courir.", color: "bg-green-600", rating: 5 },
  { id: "static-student-2", initials: "AL", name: "Amélie L.", role: "Élève", quote: "Checklist compléments, conseils IA selon mes repas, et je logue mes séances vélo avec les watts cibles. Mon coach voit tout.", color: "bg-emerald-600", rating: 5 },
  { id: "static-student-3", initials: "KP", name: "Karim P.", role: "Élève powerlifting", quote: "Les % du TM sur mon plan force, le journal de séances et les vidéos exercices — j'ai enfin un suivi structuré sans tableur Excel.", color: "bg-slate-700", rating: 5 },
];

function TestimonialCard({ t }) {
  return (
    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-shadow h-full">
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: 5 }).map((_, j) => (
          <Star key={j} className={`w-4 h-4 ${j < (t.rating || 5) ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
        ))}
      </div>
      <p className="text-slate-600 text-sm leading-relaxed mb-6">"{t.quote}"</p>
      <div className="flex items-center gap-3">
        {t.picture ? (
          <img src={t.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className={`w-10 h-10 ${t.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
            {t.initials}
          </div>
        )}
        <div>
          <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
          <p className="text-xs text-slate-500">{t.role}</p>
        </div>
      </div>
    </div>
  );
}

function mapStudentReview(r) {
  return {
    id: r.review_id,
    name: r.student_name,
    role: r.coach_name ? `Élève · Coach ${r.coach_name}` : "Élève",
    quote: r.comment || "Excellent accompagnement avec mon coach.",
    picture: r.student_picture,
    initials: r.student_name?.[0]?.toUpperCase(),
    color: "bg-green-600",
    rating: r.rating || 5,
  };
}

function mapCoachPlatformReview(r) {
  return {
    id: r.review_id,
    name: r.coach_name,
    role: "Coach MyCoaching",
    quote: r.comment || "Recommande la plateforme MyCoaching.",
    picture: r.coach_picture,
    initials: r.coach_name?.[0]?.toUpperCase(),
    color: "bg-blue-600",
    rating: r.rating || 5,
  };
}

const faqItems = [
  { q: "L'essai gratuit est-il vraiment sans engagement ?", a: "Oui, vous bénéficiez de 14 jours d'essai gratuit sans carte bancaire requise. Si vous ne souscrivez pas à un abonnement, votre compte reste actif avec les fonctionnalités de base." },
  { q: "Quelle est la différence entre Basic et Pro ?", a: "Le plan Basic convient pour démarrer avec jusqu'à 5 élèves. Le plan Pro débloque les élèves illimités, l'IA complète, le training multi-sports, l'adhérence séances, les rapports mensuels, Stripe, le bilan sanguin et le suivi photo avancé." },
  { q: "Mes élèves doivent-ils payer ?", a: "Non, vos élèves ont un accès gratuit : plans alimentaires, compléments, liste de courses, journal repas, programme multi-sports, journal de séances, check-ins, messagerie et assistant IA." },
  { q: "Comment fonctionnent les disciplines d'entraînement ?", a: "La discipline (force, course, vélo, natation, hybride ou général) est définie sur le plan, pas sur l'élève. Vous prescrivez en kg/% TM, allure, watts FTP ou CSS selon le sport. Un plan hybride peut mixer plusieurs modalités dans la même semaine." },
  { q: "Qu'est-ce que l'adhérence entraînement ?", a: "Le système compare les séances loguées par l'élève au nombre de séances prévues dans son programme sur 30 jours. Le coach voit le % par élève, avec km, volume force et badge de discipline." },
  { q: "Comment fonctionnent les charges de demain ?", a: "Chaque soir, l'élève reçoit un rappel (notification push et carte dashboard) avec les cibles du lendemain : charges en kg, allure cible, watts FTP, etc., calculées depuis ses références personnelles." },
  { q: "Comment fonctionne la liste de courses ?", a: "La plateforme agrège automatiquement les ingrédients du plan alimentaire et les compléments prescrits sur 3, 7 ou 14 jours. L'élève peut copier la liste, l'envoyer sur WhatsApp ou la télécharger." },
  { q: "Qu'est-ce que la corrélation repas ↔ compléments ?", a: "L'IA analyse le journal repas réel de l'élève sur 7 jours et son plan compléments pour proposer des horaires adaptés — par exemple prendre les oméga-3 au dîner si c'est le repas le plus riche en lipides." },
  { q: "Comment fonctionne l'assistant IA ?", a: "L'IA génère plans alimentaires, programmes training (par discipline), plans compléments, substitution d'exercices en cas de blessure, grilles tarifaires, calcule les macros et répond aux questions des élèves via le chat." },
  { q: "Puis-je gérer plusieurs comptes ?", a: "Oui, coachs et élèves peuvent enregistrer plusieurs comptes et basculer entre eux sans se reconnecter. Les jetons sont chiffrés localement sur l'appareil." },
  { q: "Puis-je annuler à tout moment ?", a: "Absolument. Aucun engagement : vous pouvez annuler votre abonnement à tout moment depuis votre espace coach. Votre accès reste actif jusqu'à la fin de la période en cours." },
  { q: "Mes données sont-elles sécurisées ?", a: "Vos données sont hébergées en Europe et protégées conformément au RGPD. Les communications sont chiffrées (SSL/TLS) et les paiements sont sécurisés via Stripe." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [latestReviews, setLatestReviews] = useState([]);
  const [latestCoachReviews, setLatestCoachReviews] = useState([]);
  const isLoggedIn = !!user || hasStoredSession();

  const goToApp = useCallback(() => {
    if (user) {
      navigate(getDashboardPath(user.role));
      return;
    }
    navigate("/dashboard");
  }, [navigate, user]);

  const goToSignup = useCallback(() => {
    if (isLoggedIn) {
      goToApp();
      return;
    }
    navigate("/login");
  }, [goToApp, isLoggedIn, navigate]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews/latest`)
      .then(r => setLatestReviews(r.data.reviews || []))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/platform-reviews/latest`)
      .then(r => setLatestCoachReviews(r.data.reviews || []))
      .catch(() => {});
  }, []);

  const coachTestimonials = useMemo(() => {
    if (latestCoachReviews.length > 0) {
      return latestCoachReviews.map(mapCoachPlatformReview).slice(0, 3);
    }
    return staticCoachTestimonials;
  }, [latestCoachReviews]);

  const studentTestimonials = useMemo(() => {
    if (latestReviews.length > 0) {
      return latestReviews.map(mapStudentReview).slice(0, 3);
    }
    return staticStudentTestimonials;
  }, [latestReviews]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold tracking-tight uppercase text-slate-900" data-testid="brand-logo">COACH.</h1>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-500">
            <button type="button" onClick={() => document.getElementById('nouveautes')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-slate-900 transition-colors">Nouveautés</button>
            <button type="button" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-slate-900 transition-colors">Fonctionnalités</button>
            <button type="button" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-slate-900 transition-colors">Tarifs</button>
          </nav>
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <Button
                onClick={goToApp}
                disabled={authLoading && !user}
                data-testid="header-account-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
              >
                Mon compte
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn" className="text-slate-600 hover:text-slate-900">
                  Connexion
                </Button>
                <Button onClick={() => navigate("/login")} data-testid="header-signup-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">
                  Commencer
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-50 text-violet-700 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" /> 2026 — Multi-sports, nutrition & adhérence
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight uppercase text-slate-900 leading-[0.95]" data-testid="hero-title">
              Transformez<br />vos clients
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
              La plateforme tout-en-un pour coachs exigeants : nutrition, compléments, training multi-sports, journal de séances, adhérence et IA — du premier rendez-vous au suivi quotidien.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {disciplinePills.map(({ icon: Icon, label, color }) => (
                <span key={label} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 shadow-sm ${color}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </span>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Adhérence séances", "Charges demain", "Compléments IA", "Bilan sanguin", "PDF brandés"].map((chip) => (
                <span key={chip} className="text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1">
                  {chip}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={goToSignup} data-testid="hero-cta-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-12 text-base shadow-lg shadow-blue-500/20 active:scale-95 transition-transform">
                {isLoggedIn ? "Accéder à mon espace" : "Démarrer gratuitement"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById('nouveautes')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-nouveautes-btn" className="rounded-full px-8 h-12 text-base border-slate-200 hover:bg-slate-50">
                Voir les nouveautés
              </Button>
              <Button variant="ghost" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-pricing-btn" className="rounded-full px-6 h-12 text-base text-slate-500 hover:text-slate-900">
                Tarifs
              </Button>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-200/50">
              <img src="https://images.pexels.com/photos/6339317/pexels-photo-6339317.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Coach en action" className="w-full h-[480px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-emerald-100 max-w-[240px]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
                  <Footprints className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Plan Course</p>
                  <p className="text-xs text-slate-500">10 km @ 4:45/km · Z2</p>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-lg border border-violet-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-white" />
                </div>
                <p className="text-xs font-semibold text-slate-800">Adhérence 87% · 30j</p>
              </div>
            </div>
            <div className="absolute top-1/2 -left-8 bg-white rounded-2xl p-3 shadow-lg border border-sky-100 hidden xl:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <p className="text-xs font-semibold text-slate-800">Demain — 220 W @ 88% FTP</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-slate-900 py-6 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <p className="text-3xl md:text-4xl font-bold text-white">500+</p>
            <p className="text-sm text-slate-400 mt-1">Coachs actifs</p>
          </div>
          <div>
            <p className="text-3xl md:text-4xl font-bold text-white">6</p>
            <p className="text-sm text-slate-400 mt-1">Disciplines training</p>
          </div>
          <div>
            <p className="text-3xl md:text-4xl font-bold text-white">10 000+</p>
            <p className="text-sm text-slate-400 mt-1">Séances loguées</p>
          </div>
          <div>
            <p className="text-3xl md:text-4xl font-bold text-white">98%</p>
            <p className="text-sm text-slate-400 mt-1">Satisfaction</p>
          </div>
        </div>
      </section>

      {/* Nouveautés */}
      <section id="nouveautes" className="py-20 px-6 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-100 text-violet-800 rounded-full text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" /> Nouveautés 2026
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Tout ce qu&apos;on a shipé</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-3xl mx-auto">
              Multi-sports, adhérence séances, nutrition intelligente, medical et compte — pour que vos élèves appliquent vraiment le plan, et que vous voyiez qui suit.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {newFeatures.map((f, i) => {
              const palette = newFeatureColors[f.color] || newFeatureColors.blue;
              return (
                <div
                  key={i}
                  className={`relative p-6 rounded-2xl border bg-gradient-to-br ${palette.card} hover:shadow-lg transition-shadow`}
                >
                  <span className={`absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full ${palette.tag}`}>
                    {f.tag}
                  </span>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${palette.icon}`}>
                    <f.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 pr-16">{f.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coach vs Élève */}
      <section className="py-20 px-6 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Deux espaces, un même suivi</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto">Le coach pilote, l&apos;élève exécute — nutrition, compléments, training multi-sports et adhérence restent synchronisés.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50/80 to-white p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900">Espace Coach</h3>
                  <p className="text-sm text-slate-500">Prescrire, suivre, analyser</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {coachHighlights.map((item, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-xl bg-white/80 border border-blue-100/80">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900">Espace Élève</h3>
                  <p className="text-sm text-slate-500">Appliquer, cocher, progresser</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {studentHighlights.map((item, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-xl bg-white/80 border border-emerald-100/80">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Tout ce dont vous avez besoin</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto">Nutrition, compléments, training multi-sports, medical, paiements et IA — une seule plateforme pour votre activité.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} className={`${f.span} group p-6 rounded-2xl border transition-all cursor-default ${
                f.highlight
                  ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-500/10'
                  : 'bg-slate-50 border-slate-100 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-500/5'
              }`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                  f.highlight
                    ? 'bg-purple-100 group-hover:bg-purple-600'
                    : 'bg-blue-100 group-hover:bg-blue-600'
                }`}>
                  <f.icon className={`w-6 h-6 group-hover:text-white transition-colors ${f.highlight ? 'text-purple-600' : 'text-blue-600'}`} />
                </div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="font-heading text-xl font-semibold text-slate-900">{f.title}</h3>
                  {f.highlight && f.badge && (
                    <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      {f.badge}
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-20 px-6 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Comment ça marche</h2>
            <p className="mt-4 text-lg text-slate-500">Lancez-vous en 3 étapes simples</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting arrows (desktop only) */}
            <div className="hidden md:block absolute top-16 left-[33%] w-[34%] border-t-2 border-dashed border-blue-200" />
            <div className="hidden md:block absolute top-16 right-[0%] w-[34%] border-t-2 border-dashed border-blue-200" />

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-5 relative">
                <UserPlus className="w-7 h-7 text-blue-600" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">1</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Inscrivez-vous</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Créez votre compte coach en 30 secondes. 14 jours d'essai gratuit, sans carte bancaire.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-5 relative">
                <Settings className="w-7 h-7 text-blue-600" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">2</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Configurez</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Ajoutez vos élèves, choisissez la discipline (force, course, vélo…), créez plans diet, compléments et programmes — ou laissez l&apos;IA vous aider.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-5 relative">
                <Rocket className="w-7 h-7 text-blue-600" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">3</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Coachez</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Suivez adhérence, séances loguées et progression — échangez via la messagerie et développez votre activité.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Macro Calculator */}
      <MacroCalculator />

      {/* Témoignages */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Ils nous font confiance</h2>
            <p className="mt-4 text-lg text-slate-500">Découvrez ce que nos utilisateurs disent de Coach Platform.</p>
          </div>

          <div className="space-y-14">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
                <span className="inline-flex items-center gap-2 self-start px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold">
                  <Layers className="w-4 h-4" />
                  Coachs
                </span>
                <p className="text-slate-500 text-sm">Ce qu'ils pensent de la plateforme MyCoaching</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {coachTestimonials.map((t) => (
                  <TestimonialCard key={t.id} t={t} />
                ))}
              </div>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
                <span className="inline-flex items-center gap-2 self-start px-4 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-semibold">
                  <Users className="w-4 h-4" />
                  Élèves
                </span>
                <p className="text-slate-500 text-sm">Ce qu'ils disent de leur accompagnement</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {studentTestimonials.map((t) => (
                  <TestimonialCard key={t.id} t={t} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Tarifs coach</h2>
            <p className="mt-4 text-lg text-slate-500">Lancez votre activité avec 14 jours d'essai gratuit. Sans engagement.</p>

            {/* Toggle Mensuel / Annuel */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8" data-testid="pricing-toggle">
              <button
                type="button"
                onClick={() => setAnnual(false)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  !annual
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Mensuel
              </button>
              <button
                type="button"
                onClick={() => setAnnual(true)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  annual
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Annuel
                <span className="text-xs font-bold text-orange-500">-2 mois</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {coachPlans.map((p) => (
              <div key={p.id} className={`relative bg-white rounded-2xl p-8 border ${p.popular ? 'border-blue-500 ring-4 ring-blue-500/10 md:scale-105' : 'border-slate-200'} transition-all hover:shadow-lg`} data-testid={`pricing-card-${p.id}`}>
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Populaire
                  </div>
                )}
                <h3 className="font-heading text-2xl font-bold text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.description}</p>

                <div className="mt-5 flex items-baseline gap-1 min-h-[2.75rem]">
                  <span className="text-4xl font-bold text-slate-900 tabular-nums">{annual ? p.annualMonthly : p.monthlyPrice}</span>
                  <span className="text-slate-500">&euro;/mois</span>
                </div>
                <p className={`text-sm text-slate-400 mt-1 h-5 transition-opacity duration-200 ${annual ? "opacity-100" : "opacity-0"}`}>
                  Factur&eacute; {p.annualPrice}&euro;/an
                </p>

                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                  <Zap className="w-3.5 h-3.5" /> 14 jours d'essai gratuit
                </div>

                <ul className="mt-6 space-y-3">
                  {p.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-blue-600" />
                      </div>
                      {feat}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={goToSignup}
                  className={`w-full mt-8 rounded-full h-11 ${p.popular ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}
                  data-testid={`pricing-cta-${p.id}`}
                >
                  {isLoggedIn ? "Mon compte" : "Essayer gratuitement"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" /> FAQ
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Questions fréquentes</h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium text-slate-900 text-sm pr-4">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">Prêt à coacher autrement ?</h2>
          <p className="mt-4 text-lg text-slate-500">Nutrition, multi-sports et adhérence — rejoignez des centaines de coachs sur MyCoaching.</p>
          <Button onClick={goToSignup} data-testid="footer-cta-btn" className="mt-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-10 h-12 text-base shadow-lg shadow-blue-500/20">
            {isLoggedIn ? "Accéder à mon espace" : "Créer mon compte coach"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-heading text-lg font-bold uppercase text-slate-400">COACH.</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
            <Link to="/mentions-legales" className="hover:text-blue-600 transition-colors">Mentions l&eacute;gales</Link>
            <span>&middot;</span>
            <Link to="/politique-confidentialite" className="hover:text-blue-600 transition-colors">Confidentialit&eacute;</Link>
            <span>&middot;</span>
            <Link to="/cgu" className="hover:text-blue-600 transition-colors">CGU</Link>
          </div>
          <p className="text-sm text-slate-400">&copy; 2026 Coach. Tous droits r&eacute;serv&eacute;s.</p>
        </div>
      </footer>
    </div>
  );
}
